import { getDirectRelationship } from '@cognite/apm-client';
import type {
  Checklist,
  ChecklistItem,
  ChecklistItemResponseType,
  ChecklistItemUpsertType,
  ChecklistResponseType,
  ChecklistUpsertType,
  IAPMAppDataService,
  ICDFView,
  InFieldUser,
} from '@cognite/apm-client';
import { FDMClient } from '@cognite/fdm-client';
import type { Filters, Sort } from '@cognite/fdm-client';
import { parseFilters } from '@cognite/fdm-client/src/utils/parse-filters';
import type { CogniteClient } from '@cognite/sdk';

import { gql } from '../../utils/graphql-request';
import { TEMPLATE_TASK_LIMIT } from '../template/constants';

import type { IChecklistService } from './checklist-service-interface';
import { valueAsInstanceId } from './hooks/utils/type-utils';
import type { ChecklistAggregate } from './types';

type APMClientDependencies = {
  checklists: IAPMAppDataService<ChecklistResponseType>;
  checklistItems: IAPMAppDataService<ChecklistItemResponseType>;
  checklistsDMS: ICDFView<Checklist>;
  appDataModelSpace: string;
  appDataModelName: string;
  appDataModelVersion: string;
  appDataInstanceSpace: string;
  userInstanceSpace: string;
  supportsSolutionTags: () => boolean;
};

export class ChecklistService extends FDMClient implements IChecklistService {
  apmClient: APMClientDependencies;
  modelSpace: string;
  modelName: string;
  modelVersion: string;
  instanceSpace: string;
  constructor(client: CogniteClient, apmClient: APMClientDependencies) {
    super(client);
    this.apmClient = apmClient;
    this.modelSpace = apmClient.appDataModelSpace;
    this.modelName = apmClient.appDataModelName;
    this.modelVersion = apmClient.appDataModelVersion;
    this.instanceSpace = apmClient.appDataInstanceSpace;
  }

  async getChecklists(
    filters?: Filters,
    sort?: Sort,
    nextCursor?: string,
    /*
    For each page with checklists we fetch aggregated statuses based on checklist items.
    We cannot get more than 10,000 checklist items as a response of a single request, so we need to limit the page size.
    From InField UI we can only create checklists with 500 items (search for TEMPLATE_TASK_LIMIT).
    20 * 500 = 10,000, so by splitting it into 20 items per page, we will make sure that we get all of the items.
    */
    pageSize = 20
  ): Promise<{
    items: Checklist[];
    pageInfo: { endCursor: string; hasNextPage: boolean };
  }> {
    const { list } = await this.apmClient.checklists.list(
      `
      externalId
      sourceId
      title
      type
      status
      startTime
      endTime
      assignedTo
      isArchived
      createdBy {
        externalId
        name
        email
      }
      updatedBy {
        externalId
        name
        email
      }
      ${
        this.apmClient.supportsSolutionTags()
          ? `solutionTags {
              items {
                externalId
                name
              }
            }
            `
          : ''
      }
      `,
      { filters, sort, nextCursor, pageSize }
    );

    const checklists: Checklist[] = list.items.map((checklist) => ({
      ...checklist,
      solutionTags: checklist.solutionTags?.items,
      checklistItems: checklist.checklistItems?.items?.map((checklistItem) => ({
        ...checklistItem,
        observations: checklistItem.observations?.items,
        measurements: checklistItem.measurements?.items,
      })),
    }));

    return {
      items: checklists,
      pageInfo: list.pageInfo,
    };
  }

  async searchChecklists(
    _filters?: Filters,
    _sort?: Sort,
    _limit?: number,
    _query?: string,
    _cursor?: string,
    _properties?: string[]
  ): Promise<{ items: Checklist[]; nextCursor?: string }> {
    throw new Error('ChecklistService.searchChecklists is not supported.');
  }

  async getChecklistsAndChecklistItems(
    filters?: Filters,
    sort?: Sort,
    nextCursor?: string,
    pageSize?: number
  ): Promise<{
    items: Checklist[];
    pageInfo: { endCursor: string; hasNextPage: boolean };
  }> {
    const { list } = await this.apmClient.checklists.list(
      `
      externalId
      sourceId
      title
      type
      status
      startTime
      endTime
      assignedTo
      isArchived
      checklistItems {
        items {
          externalId
          title
          description
          labels
          order
          status
          sourceId
          createdBy {
            externalId
            name
            email
          }
          updatedBy {
            externalId
            name
            email
          }
          asset {
            externalId
            title
            description
            space
          }
          lastUpdatedTime
        }
      }
      createdBy {
        externalId
        name
        email
      }
      updatedBy {
        externalId
        name
        email
      }
      `,
      { filters, sort, nextCursor, pageSize }
    );

    const returnedList = list as {
      items: ChecklistResponseType[];
      pageInfo: { endCursor: string; hasNextPage: boolean };
    };

    const checklists = returnedList.items.map((checklist) => ({
      ...checklist,
      checklistItems: checklist.checklistItems?.items,
    })) as Checklist[];

    return {
      items: checklists,
      pageInfo: list.pageInfo,
    };
  }

  async getChecklistData(
    checklistFilter?: Filters,
    checklistSort?: Sort,
    itemsFilter?: Filters,
    itemsSort?: Sort,
    isObservationsEnabled?: boolean,
    pageSize = 1000
  ): Promise<Checklist> {
    const {
      listChecklist: {
        items: [checklist],
      },
    } = await super.graphQL<{
      listChecklist: {
        items: ChecklistResponseType[];
      };
    }>(
      gql`
        query GetChecklistData(
          $checklistFilter: _ListChecklistFilter
          $checklistSort: [_ChecklistSort!]
          $itemsFilter: _ListChecklistItemFilter
          $itemsSort: [_ChecklistItemSort!]
          $pageSize: Int
        ) {
          listChecklist(filter: $checklistFilter, sort: $checklistSort) {
            items {
              externalId
              sourceId
              title
              type
              status
              startTime
              endTime
              assignedTo
              isArchived
              createdBy {
                externalId
                name
                email
              }
              updatedBy {
                externalId
                name
                email
              }
              checklistItems(
                filter: $itemsFilter
                sort: $itemsSort
                first: $pageSize
              ) {
                items {
                  externalId
                  title
                  description
                  labels
                  order
                  status
                  note
                  createdBy {
                    externalId
                    name
                    email
                  }
                  updatedBy {
                    externalId
                    name
                    email
                  }
                  asset {
                    externalId
                    title
                    description
                    space
                  }
                  files {
                    externalId
                  }
                  measurements (sort: {order: ASC}) {
                    items {
                      type
                      externalId
                      description
                      title
                      timeseries {
                        externalId
                        id
                        assetId
                        name
                        description
                        unit
                      }
                      measuredAt
                      min
                      max
                      numericReading
                      stringReading
                      order
                      options
                    }
                  }
                  lastUpdatedTime
                  ${
                    isObservationsEnabled
                      ? `
                      observations (filter: {isArchived: {isNull: true}}) {
                        items {
                          externalId,
                        }
                      }
                    `
                      : ''
                  }
                }
              }
            }
          }
        }
      `,
      this.modelSpace,
      this.modelName,
      this.modelVersion,
      {
        checklistFilter: parseFilters({
          and: [
            { ...checklistFilter },
            {
              equals: {
                property: 'space',
                eq: this.instanceSpace,
              },
            },
          ],
        }),
        checklistSort,
        itemsFilter: parseFilters(itemsFilter),
        itemsSort,
        pageSize,
      }
    );

    if (checklist === undefined) {
      throw new Error('Checklist not found');
    }

    return {
      ...checklist,
      solutionTags: checklist.solutionTags?.items,
      checklistItems: checklist.checklistItems?.items?.map((checklistItem) => ({
        ...checklistItem,
        observations: checklistItem.observations?.items,
        measurements: checklistItem.measurements?.items,
      })),
    };
  }

  async aggregate(
    field: string,
    isDirectRelation: boolean,
    filters?: Filters
  ): Promise<ChecklistAggregate[]> {
    const res = await this.apmClient.checklistsDMS.aggregate({
      filters,
      aggregates: [{ count: { property: field } }],
      groupBy: [field],
      searchOperator: 'AND',
    });

    return res.items.map((item) => {
      const itemField = item.group?.[field];
      const aggregateField = item.aggregates.find(
        (x) => x.aggregate === 'count'
      );
      const isAggregatedNumberValue =
        aggregateField && 'value' in aggregateField;

      return {
        value: isDirectRelation
          ? valueAsInstanceId(itemField) // Return value from Cognite SDK is not accounting for direct relationships
          : itemField,
        count: isAggregatedNumberValue ? aggregateField.value : undefined,
      };
    });
  }

  async createChecklist(checklist: Checklist, user: InFieldUser) {
    const upsertedChecklist: Checklist = {
      ...checklist,
      createdBy: user,
    };

    return this.upsertChecklist([upsertedChecklist]);
  }

  async updateChecklists(checklists: Checklist[], user: InFieldUser) {
    const upsertedChecklists: Checklist[] = checklists.map((checklist) => ({
      ...checklist,
      updatedBy: user,
    }));

    return this.upsertChecklist(upsertedChecklists);
  }

  async upsertChecklist(checklists: Checklist[]) {
    const upsertedChecklists: ChecklistUpsertType[] = checklists.map(
      (checklist) => ({
        ...checklist,
        updatedBy: checklist.updatedBy?.externalId
          ? {
              externalId: checklist.updatedBy?.externalId,
              space: this.apmClient.userInstanceSpace,
            }
          : undefined,
        createdBy: checklist.createdBy?.externalId
          ? {
              externalId: checklist.createdBy?.externalId,
              space: this.apmClient.userInstanceSpace,
            }
          : undefined,
        rootLocation:
          checklist.rootLocation && checklist.rootLocation.space
            ? {
                space: checklist.rootLocation.space,
                externalId: checklist.rootLocation.externalId,
              }
            : undefined,
        solutionTags: checklist?.solutionTags?.map((tag) => ({
          externalId: tag.externalId,
          space: this.apmClient.appDataInstanceSpace,
        })),
      })
    );
    await this.apmClient.checklists.upsert(upsertedChecklists);
    return checklists;
  }

  async getChecklistItemObservations(
    filters?: Filters,
    sort?: Sort,
    pageSize = TEMPLATE_TASK_LIMIT
  ): Promise<ChecklistItem[]> {
    const {
      list: { items },
    } = await this.apmClient.checklistItems.list(
      `
      externalId
      asset {
        externalId
        title
        space
      }
        observations (filter: {isArchived: {isNull: true}}){
          items {
            externalId
            status
            createdBy {
              externalId
              name
              email
            }
            updatedBy {
              externalId
              name
              email
            }
            createdTime
            files {
              externalId
              downloadLink {
                downloadUrl
              }
            }
          }
        }
      `,
      {
        filters,
        sort,
        pageSize,
      }
    );

    return items.map((checklistItem) => ({
      ...checklistItem,
      observations: checklistItem.observations?.items,
      measurements: checklistItem.measurements?.items,
    })) satisfies ChecklistItem[];
  }

  async getChecklistItemsStatus(
    checklistFilter?: Filters,
    checklistSort?: Sort,
    itemsFilter?: Filters,
    itemsSort?: Sort,
    pageSize = 1000
  ): Promise<Checklist[]> {
    const {
      listChecklist: { items: checklists },
    } = await super.graphQL<{
      listChecklist: {
        items: ChecklistResponseType[];
      };
    }>(
      gql`
        query GetChecklistItemStatuses(
          $checklistFilter: _ListChecklistFilter
          $checklistSort: [_ChecklistSort!]
          $itemsFilter: _ListChecklistItemFilter
          $itemsSort: [_ChecklistItemSort!]
          $pageSize: Int
        ) {
          listChecklist(
            filter: $checklistFilter
            sort: $checklistSort
            first: $pageSize
          ) {
            items {
              externalId
              checklistItems(
                filter: $itemsFilter
                sort: $itemsSort
                first: $pageSize
              ) {
                items {
                  externalId
                  status
                }
              }
            }
          }
        }
      `,
      this.modelSpace,
      this.modelName,
      this.modelVersion,
      {
        checklistFilter: parseFilters({
          and: [
            { ...checklistFilter },
            {
              equals: {
                property: 'space',
                eq: this.instanceSpace,
              },
            },
          ],
        }),
        checklistSort,
        itemsFilter: parseFilters(itemsFilter),
        itemsSort,
        pageSize,
      }
    );

    return checklists.map((checklist) => ({
      ...checklist,
      solutionTags: checklist.solutionTags?.items,
      checklistItems: checklist.checklistItems?.items?.map((checklistItem) => ({
        ...checklistItem,
        observations: checklistItem.observations?.items,
        measurements: checklistItem.measurements?.items,
      })),
    }));
  }

  async createChecklistItems(
    checklistItems: ChecklistItem[],
    user: InFieldUser
  ) {
    const createdChecklistItems: ChecklistItem[] = checklistItems.map(
      (checklistItem) => ({
        ...checklistItem,
        createdBy: user,
      })
    );

    return this.upsertChecklistItems(createdChecklistItems);
  }

  async updateChecklistItems(
    checklistItems: ChecklistItem[],
    user: InFieldUser
  ) {
    const updatedChecklistItems: ChecklistItem[] = checklistItems.map(
      (checklistItem) => ({
        ...checklistItem,
        updatedBy: user,
      })
    );

    return this.upsertChecklistItems(updatedChecklistItems);
  }

  async upsertChecklistItems(checklistItems: ChecklistItem[]) {
    const upsertedChecklistItem: ChecklistItemUpsertType[] = checklistItems.map(
      (checklistItem) => {
        const assetRelationship = getDirectRelationship(checklistItem.asset);
        return {
          ...checklistItem,
          files:
            checklistItem.files
              ?.filter((file) => file?.externalId)
              .map((file) => file!.externalId) || undefined,
          asset: assetRelationship,
          createdBy: checklistItem.createdBy?.externalId
            ? {
                externalId: checklistItem.createdBy?.externalId,
                space: this.apmClient.userInstanceSpace,
              }
            : undefined,
          updatedBy: checklistItem.updatedBy?.externalId
            ? {
                externalId: checklistItem.updatedBy?.externalId,
                space: this.apmClient.userInstanceSpace,
              }
            : undefined,
        };
      }
    );
    return this.apmClient.checklistItems.upsert(upsertedChecklistItem);
  }

  async softDeleteChecklists(externalIds: string[]) {
    const checklists: Checklist[] = externalIds.map((externalId) => ({
      externalId,
      isArchived: true,
    }));
    await this.apmClient.checklists.upsert(checklists);
    return externalIds;
  }

  async deleteChecklist(externalIds: string[]) {
    return this.apmClient.checklists.delete(externalIds);
  }

  async deleteChecklistItems(externalIds: string[]) {
    return this.apmClient.checklistItems.delete(externalIds);
  }
}

export const createChecklistService = (
  ...args: ConstructorParameters<typeof ChecklistService>
): IChecklistService => new ChecklistService(...args);
